import React, { useState, useEffect } from "react";
import { MapPin, ExternalLink, Phone, Info } from "lucide-react";
import { useParams } from "react-router-dom";
import { venueAPI } from "../services/api";
import "./VenueMap.css";

const VenueMap = () => {
  const { eventId } = useParams();
  const [venue, setVenue] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (eventId) {
      fetchVenue();
    }
  }, [eventId]);

  const fetchVenue = async () => {
    setLoading(true);
    try {
      const response = await venueAPI.getByEvent(parseInt(eventId));
      setVenue(response.data);
    } catch (error) {
      console.error("Failed to fetch venue:", error);
    } finally {
      setLoading(false);
    }
  };

  // Generate Google Maps embed URL using address or name
  const getEmbedUrl = (venue) => {
    if (!venue) return null;

    const query = venue.address || venue.name;
    if (!query) return null;

    const encodedQuery = encodeURIComponent(query);
    return `https://maps.google.com/maps?q=${encodedQuery}&t=&z=15&ie=UTF8&iwloc=&output=embed`;
  };

  if (loading) {
    return (
      <div className="venue-map-page">
        <div style={{ textAlign: "center", padding: "40px", color: "#64748b" }}>
          Loading venue information...
        </div>
      </div>
    );
  }

  if (!venue) {
    return (
      <div className="venue-map-page">
        <div
          className="empty-state"
          style={{ textAlign: "center", padding: "60px 20px" }}
        >
          <MapPin size={48} style={{ opacity: 0.2, margin: "0 auto 16px" }} />
          <h3>No Venue Information</h3>
          <p style={{ color: "#64748b" }}>
            Venue details for this event are not available yet.
          </p>
        </div>
      </div>
    );
  }

  const embedUrl = getEmbedUrl(venue);

  return (
    <div className="venue-map-page">
      {/* Venue Header */}
      <div
        style={{
          background: "white",
          padding: "24px",
          borderBottom: "1px solid #e2e8f0",
        }}
      >
        <h1
          style={{
            fontSize: "24px",
            fontWeight: "700",
            margin: "0 0 8px 0",
            color: "#0f172a",
          }}
        >
          {venue.name}
        </h1>
        {venue.address && (
          <div
            style={{
              display: "flex",
              alignItems: "start",
              gap: "8px",
              color: "#64748b",
              marginTop: "12px",
            }}
          >
            <MapPin size={18} style={{ marginTop: "2px", flexShrink: 0 }} />
            <span style={{ fontSize: "14px" }}>{venue.address}</span>
          </div>
        )}
      </div>

      {/* Google Maps */}
      {venue.google_maps_url && (
        <div
          style={{
            background: "white",
            padding: "16px",
            borderBottom: "1px solid #e2e8f0",
          }}
        >
          {embedUrl ? (
            <div
              style={{ display: "flex", flexDirection: "column", gap: "12px" }}
            >
              <iframe
                src={embedUrl}
                width="100%"
                height="400"
                style={{ border: 0, borderRadius: "8px" }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Venue Map"
              />
              <div style={{ textAlign: "right" }}>
                <a
                  href={venue.google_maps_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    fontSize: "14px",
                    color: "#3b82f6",
                    textDecoration: "none",
                    fontWeight: "600",
                  }}
                >
                  <ExternalLink size={16} />
                  Open in Google Maps
                </a>
              </div>
            </div>
          ) : (
            <div
              style={{
                height: "400px",
                background: "#f1f5f9",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                gap: "12px",
              }}
            >
              <MapPin size={48} color="#94a3b8" />
              <a
                href={venue.google_maps_url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "12px 24px",
                  background: "#3b82f6",
                  color: "white",
                  borderRadius: "8px",
                  textDecoration: "none",
                  fontWeight: "500",
                }}
              >
                <ExternalLink size={18} />
                Open in Google Maps
              </a>
            </div>
          )}
        </div>
      )}

      {/* Venue Details */}
      <div style={{ background: "white", padding: "24px" }}>
        {venue.description && (
          <div style={{ marginBottom: "24px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "12px",
              }}
            >
              <Info size={18} color="#3b82f6" />
              <h3 style={{ fontSize: "16px", fontWeight: "600", margin: 0 }}>
                About the Venue
              </h3>
            </div>
            <p
              style={{
                fontSize: "14px",
                color: "#475569",
                lineHeight: "1.6",
                margin: 0,
                whiteSpace: "pre-line",
              }}
            >
              {venue.description}
            </p>
          </div>
        )}

        {venue.contact_info && (
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "12px",
              }}
            >
              <Phone size={18} color="#3b82f6" />
              <h3 style={{ fontSize: "16px", fontWeight: "600", margin: 0 }}>
                Contact Information
              </h3>
            </div>
            <p
              style={{
                fontSize: "14px",
                color: "#475569",
                lineHeight: "1.6",
                margin: 0,
                whiteSpace: "pre-line",
              }}
            >
              {venue.contact_info}
            </p>
          </div>
        )}

        {!venue.description && !venue.contact_info && venue.google_maps_url && (
          <div style={{ textAlign: "center", padding: "20px" }}>
            <a
              href={venue.google_maps_url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                padding: "12px 24px",
                background: "#3b82f6",
                color: "white",
                borderRadius: "8px",
                textDecoration: "none",
                fontWeight: "500",
              }}
            >
              <ExternalLink size={18} />
              View on Google Maps
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default VenueMap;
